import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Container, Card, Spinner, Alert, Button } from "react-bootstrap";
import { useActivateAccount } from "../hooks/useActivateAccount";

// A stale/already-consumed token (Djoser: 403 "Stale token for given user.")
// means the account is most likely already active — e.g. the user re-opened
// the email link, or the first attempt silently succeeded despite a dev-only
// reload interrupting the page before it could show the success state.
function isStaleTokenError(error) {
  return error?.response?.status === 403;
}

function ActivateAccount() {
  const { slug, uid, token } = useParams();
  const { mutate, isPending, isSuccess, isError, error } = useActivateAccount();
  const [showSlowNotice, setShowSlowNotice] = useState(false);
  const [giveUp, setGiveUp] = useState(false);

  // Guard against double-invocation in React 18 Strict Mode
  const triggered = useRef(false);

  const runActivation = () => {
    triggered.current = true;
    setShowSlowNotice(false);
    setGiveUp(false);
    mutate({ uid, token });
  };

  useEffect(() => {
    if (triggered.current) return;
    runActivation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, token]);

  // The request can succeed server-side (we've confirmed via logs + the
  // confirmation email) while the promise never settles in the browser — seen
  // with some ad/tracker-blocking extensions silently swallowing the response.
  // Rather than leave a dead spinner forever, escalate in two stages: a
  // reassurance at 8s, then an actionable "go to login" at 20s (activation is
  // backend-first, so by then the account is almost certainly already active).
  useEffect(() => {
    if (!isPending) {
      setShowSlowNotice(false);
      setGiveUp(false);
      return undefined;
    }
    const slow = setTimeout(() => setShowSlowNotice(true), 8000);
    const give = setTimeout(() => setGiveUp(true), 20000);
    return () => {
      clearTimeout(slow);
      clearTimeout(give);
    };
  }, [isPending]);

  return (
    <Container
      className="d-flex justify-content-center align-items-center min-vh-100"
      style={{ padding: "1rem" }}
    >
      <Card
        className="w-100 text-center shadow-sm"
        style={{ maxWidth: 440 }}
      >
        <Card.Body className="p-4">
          {isPending && !giveUp && (
            <>
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Activando tu cuenta…</p>
              {showSlowNotice && (
                <p className="text-muted small mb-0">
                  Esto está tomando más de lo normal. Si no avanza en unos
                  segundos, intenta recargar la página.
                </p>
              )}
            </>
          )}

          {isPending && giveUp && (
            <>
              <Alert variant="info" className="mb-3">
                Esto está tomando demasiado tiempo. Si recibiste el correo de
                confirmación, tu cuenta ya debería estar activa.
              </Alert>
              <div className="d-flex gap-2 justify-content-center">
                <Link to={`/${slug}/login`} className="btn btn-primary">
                  Ir a iniciar sesión
                </Link>
                <Button variant="outline-secondary" onClick={runActivation}>
                  Reintentar
                </Button>
              </div>
            </>
          )}

          {isSuccess && (
            <>
              <h5>¡Cuenta activada!</h5>
              <p className="text-muted mt-2">
                Tu cuenta está lista. Ya puedes iniciar sesión.
              </p>
              <Link
                to={`/${slug}/login`}
                className="btn btn-primary mt-2"
              >
                Iniciar sesión
              </Link>
            </>
          )}

          {isError && isStaleTokenError(error) && (
            <>
              <Alert variant="info" className="mb-3">
                Este enlace ya fue usado. Es probable que tu cuenta ya esté
                activa.
              </Alert>
              <Link to={`/${slug}/login`} className="btn btn-primary">
                Ir a iniciar sesión
              </Link>
            </>
          )}

          {isError && !isStaleTokenError(error) && (
            <>
              <Alert variant="danger" className="mb-3">
                Enlace expirado o inválido. Solicita un nuevo enlace de
                activación.
              </Alert>
              <Button variant="outline-secondary" onClick={runActivation}>
                Reintentar
              </Button>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export { ActivateAccount };
